/**
 * Canvas heatmap overlay for Google Maps using heatmap.js (h337).
 * Replaces deprecated google.maps.visualization.HeatmapLayer (removed in API v3.65).
 */
export function createGoogleHeatmapOverlay (google, options = {}) {
  if (!google || !google.maps) return null
  if (!window.h337) return null

  function GoogleHeatmapOverlay (opts) {
    this.points = opts.points || []
    this.maxIntensity = opts.maxIntensity || 5
    this.radius = opts.radius || 25
    this.opacity = opts.opacity != null ? opts.opacity : 1
    this.heatmap = null
    this.container = null
    this.setMap(opts.map || null)
  }

  GoogleHeatmapOverlay.prototype = new google.maps.OverlayView()

  GoogleHeatmapOverlay.prototype.onAdd = function onAdd () {
    this.container = document.createElement('div')
    this.container.style.position = 'absolute'
    this.container.style.pointerEvents = 'none'

    this.heatmap = window.h337.create({
      container: this.container,
      radius: this.radius,
      maxOpacity: this.opacity,
      minOpacity: 0,
      valueField: 'count',
      latField: 'lat',
      lngField: 'lng'
    })

    this.getPanes().overlayLayer.appendChild(this.container)
    this.draw()
  }

  GoogleHeatmapOverlay.prototype.onRemove = function onRemove () {
    if (this.container && this.container.parentNode) {
      this.container.parentNode.removeChild(this.container)
    }
    this.container = null
    this.heatmap = null
  }

  GoogleHeatmapOverlay.prototype.draw = function draw () {
    if (!this.heatmap || !this.container) return

    const projection = this.getProjection()
    const map = this.getMap()
    if (!projection || !map) return

    const bounds = map.getBounds()
    if (!bounds) return

    const ne = bounds.getNorthEast()
    const sw = bounds.getSouthWest()
    const topLeft = projection.fromLatLngToDivPixel(new google.maps.LatLng(ne.lat(), sw.lng()))
    const bottomRight = projection.fromLatLngToDivPixel(new google.maps.LatLng(sw.lat(), ne.lng()))

    const width = Math.max(1, bottomRight.x - topLeft.x)
    const height = Math.max(1, bottomRight.y - topLeft.y)

    this.container.style.left = `${topLeft.x}px`
    this.container.style.top = `${topLeft.y}px`
    this.container.style.width = `${width}px`
    this.container.style.height = `${height}px`

    if (this.heatmap._renderer && this.heatmap._renderer.setDimensions) {
      this.heatmap._renderer.setDimensions(width, height)
    }

    const data = []
    this.points.forEach((point) => {
      if (point.lat == null || point.lng == null) return
      const latLng = new google.maps.LatLng(Number(point.lat), Number(point.lng))
      if (!bounds.contains(latLng)) return
      const pixel = projection.fromLatLngToDivPixel(latLng)
      data.push({
        x: Math.round(pixel.x - topLeft.x),
        y: Math.round(pixel.y - topLeft.y),
        count: Number(point.count || point.weight || 1)
      })
    })

    this.heatmap.setData({
      max: this.maxIntensity || 1,
      data
    })
  }

  GoogleHeatmapOverlay.prototype.setPoints = function setPoints (points) {
    this.points = points || []
    this.draw()
  }

  GoogleHeatmapOverlay.prototype.setMaxIntensity = function setMaxIntensity (maxIntensity) {
    this.maxIntensity = maxIntensity
    this.draw()
  }

  GoogleHeatmapOverlay.prototype.setRadius = function setRadius (radius) {
    this.radius = radius
    if (this.heatmap) {
      this.heatmap.configure({ radius })
      if (this.heatmap._store) {
        this.heatmap._store._cfgRadius = radius
      }
    }
    this.draw()
  }

  GoogleHeatmapOverlay.prototype.setOpacity = function setOpacity (opacity) {
    this.opacity = opacity
    if (this.heatmap) {
      this.heatmap.configure({ maxOpacity: opacity })
    }
    this.draw()
  }

  return new GoogleHeatmapOverlay(options)
}
